// src/components/TimelineItem.jsx
import React, { useState } from 'react';
import {
  MessageSquare,
  Phone,
  Mail,
  Calendar,
  CheckSquare,
  FileText,
  Home,
  User,
  Star,
  AlertCircle,
  Clock,
  Edit2,
  Trash2,
  MoreVertical,
  Download,
  Eye,
  Link as LinkIcon,
  ChevronRight,
  Paperclip
} from 'lucide-react';
import { useIsMobile } from '../hooks/useMediaQuery'; // 👈 NUEVO IMPORT

/**
 * 🎯 Componente TimelineItem - Item individual da linha do tempo
 * 📱 ADAPTADA PARA DISPOSITIVOS MÓVILES
 * 
 * Funcionalidades:
 * - 🎨 Ícones dinâmicos por tipo de evento
 * - 📱 Layout responsivo
 * - 🖱️ Hover com ações rápidas (adaptado para touch)
 * - 🔗 Links para itens relacionados
 * - 📎 Anexos e metadados
 * - 🎯 Badges de status
 * - ✨ Animações suaves
 */

// Mapeamento de ícones por tipo de evento
const EVENT_ICONS = {
  note: MessageSquare,
  message: MessageSquare,
  whatsapp: MessageSquare,
  call: Phone,
  email: Mail,
  task: CheckSquare,
  task_completed: CheckSquare,
  meeting: Calendar,
  appointment: Calendar,
  document: FileText,
  property: Home,
  client: User,
  lead: Star,
  system: AlertCircle
};

// Cores por tipo de evento
const EVENT_COLORS = {
  note: {
    bg: 'bg-blue-100',
    text: 'text-blue-600',
    icon: 'text-blue-600',
    border: 'border-blue-200',
    light: 'bg-blue-50'
  },
  message: {
    bg: 'bg-green-100',
    text: 'text-green-600',
    icon: 'text-green-600',
    border: 'border-green-200',
    light: 'bg-green-50'
  },
  whatsapp: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-600',
    icon: 'text-emerald-600',
    border: 'border-emerald-200',
    light: 'bg-emerald-50'
  },
  call: {
    bg: 'bg-purple-100',
    text: 'text-purple-600',
    icon: 'text-purple-600',
    border: 'border-purple-200',
    light: 'bg-purple-50'
  },
  email: {
    bg: 'bg-amber-100',
    text: 'text-amber-600',
    icon: 'text-amber-600',
    border: 'border-amber-200',
    light: 'bg-amber-50'
  },
  task: {
    bg: 'bg-orange-100',
    text: 'text-orange-600',
    icon: 'text-orange-600',
    border: 'border-orange-200',
    light: 'bg-orange-50'
  },
  task_completed: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-600',
    icon: 'text-emerald-600',
    border: 'border-emerald-200',
    light: 'bg-emerald-50'
  },
  meeting: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-600',
    icon: 'text-indigo-600',
    border: 'border-indigo-200',
    light: 'bg-indigo-50'
  },
  document: {
    bg: 'bg-rose-100',
    text: 'text-rose-600',
    icon: 'text-rose-600',
    border: 'border-rose-200',
    light: 'bg-rose-50'
  },
  property: {
    bg: 'bg-cyan-100',
    text: 'text-cyan-600',
    icon: 'text-cyan-600',
    border: 'border-cyan-200',
    light: 'bg-cyan-50'
  },
  system: {
    bg: 'bg-slate-100',
    text: 'text-slate-600',
    icon: 'text-slate-600',
    border: 'border-slate-200',
    light: 'bg-slate-50'
  }
};

const DEFAULT_COLOR = {
  bg: 'bg-slate-100',
  text: 'text-slate-600',
  icon: 'text-slate-600',
  border: 'border-slate-200',
  light: 'bg-slate-50'
};

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Agora';
  if (diffMins < 60) return `${diffMins} min`;
  if (diffHours < 24) return `${diffHours} h`;
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) return `${diffDays} d`;
  
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const TimelineItem = ({
  event,
  onEdit,
  onDelete,
  onClick,
  onRelatedClick,
  isLast = false,
  showConnector = true,
  expanded = false,
  className = '',
  isMobile: propIsMobile // Permitir pasar explícitamente
}) => {
  const internalIsMobile = useIsMobile();
  const isMobile = propIsMobile !== undefined ? propIsMobile : internalIsMobile;
  
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(expanded);

  const type = event.type || 'note';
  const colors = EVENT_COLORS[type] || DEFAULT_COLOR;
  const Icon = EVENT_ICONS[type] || MessageSquare;

  const timeAgo = formatTime(event.createdAt || event.timestamp);

  const hasAttachments = event.attachments?.length > 0;
  const hasRelated = event.clientId || event.propertyId || event.taskId;
  const isTask = type === 'task' || type === 'task_completed';
  const isCompleted = event.status === 'completed' || event.completed;

  const handleItemClick = (e) => {
    if (onClick) {
      onClick(event, e);
    }
  };

  const handleRelatedClick = (e) => {
    e.stopPropagation();
    if (onRelatedClick) {
      onRelatedClick(event);
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(event);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(event);
    }
  };

  return (
    <div
      className={`relative group ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleItemClick}
    >
      {/* Línea conectora vertical - más delgada en móvil */}
      {showConnector && !isLast && (
        <div
          className={`absolute left-${isMobile ? '4' : '5'} top-${isMobile ? '8' : '10'} bottom-0 w-0.5 ${colors.bg} opacity-30`}
          style={{ height: 'calc(100% - 2rem)' }}
        />
      )}

      <div className={`flex ${isMobile ? 'gap-2' : 'gap-4'} relative`}>
        
        {/* Ícone del evento - ADAPTADO */}
        <div className="relative z-10">
          <div
            className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-xl ${colors.bg} flex items-center justify-center shadow-sm transition-transform group-hover:scale-110 touch:active:scale-95`}
          >
            <Icon className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} style={{ color: colors.icon.split('-')[1] }} />
          </div>

          {/* Badge de status para tarefas */}
          {isTask && (
            <div
              className={`absolute -top-1 -right-1 rounded-full border-2 border-white ${
                isMobile ? 'w-3 h-3' : 'w-4 h-4'
              } ${isCompleted ? 'bg-emerald-500' : 'bg-amber-500'}`}
            />
          )}
        </div>

        {/* Contenido - ADAPTADO */}
        <div className="flex-1 min-w-0 pb-4">
          <div
            className={`bg-white rounded-xl border ${colors.border} transition-all ${
              isHovered && !isMobile ? 'shadow-md scale-[1.02]' : 'shadow-sm'
            } ${onClick ? 'cursor-pointer' : ''} ${isMobile ? 'p-3' : 'p-4'}`}
          >
            
            {/* Header del evento - ADAPTADO */}
            <div className={`flex items-start justify-between gap-1 ${isMobile ? 'mb-1' : 'mb-2'}`}>
              <div className="flex-1">
                <div className="flex items-center gap-1 flex-wrap">
                  <span className={`font-medium ${colors.text} uppercase tracking-wider ${
                    isMobile ? 'text-[9px]' : 'text-xs'
                  }`}>
                    {event.typeLabel || type}
                  </span>
                  <span className={`text-slate-400 flex items-center gap-0.5 ${
                    isMobile ? 'text-[8px]' : 'text-xs'
                  }`}>
                    <Clock className={isMobile ? 'w-2 h-2' : 'w-3 h-3'} />
                    {timeAgo}
                  </span>
                  
                  {/* 🔥 CORREÇÃO: Renderização segura do usuário */}
                  {event.user && !isMobile && (
                    <>
                      <span className="text-xs text-slate-300">•</span>
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {typeof event.user === 'object' 
                          ? (event.user.name || event.user.email || `ID: ${event.user.id?.slice(-4) || ''}` || 'Usuário')
                          : String(event.user)
                        }
                      </span>
                    </>
                  )}
                </div>

                {/* Título */}
                {event.title && (
                  <h4 className={`font-medium text-slate-900 mt-0.5 ${
                    isMobile ? 'text-xs' : 'text-sm'
                  }`}>
                    {event.title}
                  </h4>
                )}
              </div>

              {/* Ações rápidas - SIEMPRE VISIBLES EN MÓVIL */}
              <div
                className={`flex gap-0.5 transition-opacity ${
                  isMobile ? 'opacity-100' : (isHovered ? 'opacity-100' : 'opacity-0')
                }`}
              >
                {hasRelated && (
                  <button
                    onClick={handleRelatedClick}
                    className={`hover:bg-slate-100 rounded-lg transition-colors touch:active:scale-95 ${
                      isMobile ? 'p-1' : 'p-1.5'
                    }`}
                    title="Ver relacionado"
                  >
                    <LinkIcon className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-slate-500`} />
                  </button>
                )}
                {onEdit && (
                  <button
                    onClick={handleEdit}
                    className={`hover:bg-blue-100 rounded-lg transition-colors touch:active:scale-95 ${
                      isMobile ? 'p-1' : 'p-1.5'
                    }`}
                    title="Editar"
                  >
                    <Edit2 className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-blue-600`} />
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={handleDelete}
                    className={`hover:bg-red-100 rounded-lg transition-colors touch:active:scale-95 ${
                      isMobile ? 'p-1' : 'p-1.5'
                    }`}
                    title="Excluir"
                  >
                    <Trash2 className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-red-600`} />
                  </button>
                )}
              </div>
            </div>

            {/* Descrição/Mensagem - ADAPTADA */}
            {event.description || event.message ? (
              <div
                className={`text-slate-600 whitespace-pre-wrap ${
                  isMobile ? 'text-[9px] mt-1' : 'text-sm mt-2'
                } ${
                  !isExpanded && (event.description?.length > (isMobile ? 100 : 200)) ? 'line-clamp-2' : ''
                }`}
              >
                {event.description || event.message}
              </div>
            ) : null}

            {/* Botón "Ver más" - ADAPTADO */}
            {event.description?.length > (isMobile ? 100 : 200) && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={`text-blue-600 hover:text-blue-700 mt-0.5 flex items-center gap-0.5 touch:active:scale-95 ${
                  isMobile ? 'text-[8px]' : 'text-xs'
                }`}
              >
                {isExpanded ? 'Ver menos' : 'Ver mais'}
                <ChevronRight
                  className={`transition-transform ${isMobile ? 'w-2 h-2' : 'w-3 h-3'} ${
                    isExpanded ? 'rotate-90' : ''
                  }`}
                />
              </button>
            )}

            {/* Anexos - ADAPTADOS */}
            {hasAttachments && (
              <div className={`${isMobile ? 'mt-2 pt-2' : 'mt-3 pt-3'} border-t border-slate-100`}>
                <p className={`text-slate-500 mb-1 flex items-center gap-0.5 ${
                  isMobile ? 'text-[8px]' : 'text-xs'
                }`}>
                  <Paperclip className={isMobile ? 'w-2 h-2' : 'w-3 h-3'} />
                  Anexos ({event.attachments.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {event.attachments.map((att, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-0.5 bg-slate-50 rounded-lg ${
                        isMobile ? 'px-1.5 py-0.5 text-[8px]' : 'px-2 py-1 text-xs'
                      }`}
                    >
                      <FileText className={`${isMobile ? 'w-2 h-2' : 'w-3 h-3'} text-slate-400`} />
                      <span className="text-slate-600 truncate max-w-[80px]">{att.name}</span>
                      {att.url && (
                        <a
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`ml-0.5 hover:bg-slate-200 rounded ${
                            isMobile ? 'p-0.5' : 'p-0.5'
                          }`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Eye className={`${isMobile ? 'w-2 h-2' : 'w-3 h-3'} text-slate-400`} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Metadados adicionais - ADAPTADOS */}
            {(event.value || event.duration || event.outcome) && (
              <div className={`flex flex-wrap gap-2 ${isMobile ? 'mt-2' : 'mt-3'} ${
                isMobile ? 'text-[8px]' : 'text-xs'
              }`}>
                {event.value && (
                  <span className="text-emerald-600 font-medium">
                    R$ {Number(event.value).toLocaleString('pt-BR')}
                  </span>
                )}
                {event.duration && (
                  <span className="text-slate-500">
                    {event.duration} min
                  </span>
                )}
                {event.outcome && (
                  <span className="flex items-center gap-0.5">
                    <Star className={`${isMobile ? 'w-2 h-2' : 'w-3 h-3'} text-amber-500`} />
                    {event.outcome}
                  </span>
                )}
              </div>
            )}

            {/* 🔥 CORREÇÃO: Tags con seguridad - ADAPTADAS */}
            {event.tags && event.tags.length > 0 && (
              <div className={`flex flex-wrap gap-0.5 ${isMobile ? 'mt-2' : 'mt-3'}`}>
                {event.tags.map((tag, idx) => {
                  if (!tag) return null;
                  
                  let tagText = '';
                  
                  try {
                    if (typeof tag === 'string') {
                      tagText = tag;
                    } else if (typeof tag === 'object' && tag !== null) {
                      tagText = tag.label || tag.name || tag.id || tag.title || tag.text || '';
                      if (!tagText) {
                        tagText = JSON.stringify(tag);
                      }
                    } else {
                      tagText = String(tag);
                    }
                  } catch {
                    tagText = 'tag';
                  }
                  
                  if (typeof tagText !== 'string') {
                    tagText = String(tagText);
                  }
                  
                  tagText = tagText.trim();
                  
                  if (!tagText) {
                    tagText = 'tag';
                  }
                  
                  return (
                    <span
                      key={`${tagText}-${idx}-${Date.now()}`}
                      className={`rounded-full ${colors.light} ${colors.text} ${
                        isMobile ? 'px-1.5 py-0.5 text-[7px]' : 'px-2 py-0.5 text-xs'
                      }`}
                    >
                      #{isMobile && tagText.length > 6 ? tagText.substring(0,5)+'…' : tagText}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Link para item relacionado */}
            {hasRelated && !isMobile && (
              <div className="mt-3 pt-2 border-t border-slate-100">
                <button
                  onClick={handleRelatedClick}
                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <LinkIcon className="w-3 h-3" />
                  Ver {event.clientId ? 'cliente' : event.propertyId ? 'imóvel' : 'detalhes'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Versão compacta para listagens rápidas - ADAPTADA
TimelineItem.Compact = ({ event, onClick, isMobile: propIsMobile }) => {
  const internalIsMobile = useIsMobile();
  const isMobile = propIsMobile !== undefined ? propIsMobile : internalIsMobile;
  
  const type = event.type || 'note';
  const colors = EVENT_COLORS[type] || DEFAULT_COLOR;
  const Icon = EVENT_ICONS[type] || MessageSquare;
  const timeAgo = formatTime(event.createdAt || event.timestamp);

  return (
    <div
      className={`flex items-center gap-2 rounded-lg hover:bg-slate-50 transition-colors touch:active:scale-95 ${
        isMobile ? 'p-1.5' : 'p-2'
      } ${onClick ? 'cursor-pointer' : ''}`}
      onClick={() => onClick?.(event)}
    >
      <div className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} rounded-lg ${colors.bg} flex items-center justify-center`}>
        <Icon className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} style={{ color: colors.icon.split('-')[1] }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-slate-900 truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>
          {event.title || event.description || event.message}
        </p>
        <p className={`text-slate-500 ${isMobile ? 'text-[8px]' : 'text-xs'}`}>{timeAgo}</p>
      </div>
    </div>
  );
};

// Versão para grupo de datas - ADAPTADA
TimelineItem.Group = ({ date, children, isMobile: propIsMobile }) => {
  const internalIsMobile = useIsMobile();
  const isMobile = propIsMobile !== undefined ? propIsMobile : internalIsMobile;

  return (
    <div className={`${isMobile ? 'mb-3' : 'mb-6'}`}>
      <div className="sticky top-0 bg-white/80 backdrop-blur-sm z-10 py-1">
        <h3 className={`font-semibold text-slate-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>
          {date instanceof Date 
            ? date.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })
            : date}
        </h3>
      </div>
      <div className={`space-y-2`}>
        {children}
      </div>
    </div>
  );
};

export default TimelineItem;